/**
 * <bilga-parts> — Ersatzteil-Finder für Bilga Kids
 *
 * Filtert die Teilekacheln nach Modell-Tag und Suchbegriff und öffnet die
 * Detailkarte als aufklappende Zeile direkt unter der angeklickten Kachel.
 *
 * Ohne JavaScript bleibt jede Kachel ein normaler Link auf die Produktseite
 * und die Filterleiste verschwindet — die Seite funktioniert also auch dann.
 *
 * Die Datei ist gekapselt, weil die Sektion mehrfach auf einer Seite stehen
 * kann und ihr <script>-Tag dann mehrfach ausgeführt wird. Ohne die Kapselung
 * bricht der zweite Durchlauf mit "Identifier has already been declared" ab.
 */
(function () {
  if (customElements.get('bilga-parts')) return;

  var REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

  class BilgaParts extends HTMLElement {
    connectedCallback() {
      // Der Theme-Editor verschiebt Sektionen, statt sie neu zu rendern —
      // dann läuft connectedCallback ein zweites Mal.
      if (this.initialised) return;

      this.grid = this.querySelector('[data-bp-grid]');
      this.panelStore = this.querySelector('[data-bp-panels]');
      this.cards = Array.from(this.querySelectorAll('[data-bp-card]'));
      this.panels = [];
      this.chips = Array.from(this.querySelectorAll('[data-bp-filter]'));
      this.searchInput = this.querySelector('[data-bp-search]');
      this.countEl = this.querySelector('[data-bp-count]');
      this.noneEl = this.querySelector('[data-bp-none]');
      if (!this.grid || !this.panelStore || !this.cards.length) return;

      // Nach data-bp-panel indizieren statt nach Dokumentreihenfolge: eine
      // offene Karte liegt im Raster und nicht mehr im Panel-Container.
      this.querySelectorAll('[data-bp-panel]').forEach(function (panel) {
        this.panels[Number(panel.dataset.bpPanel)] = panel;
      }, this);

      this.initialised = true;
      this.filter = '';
      this.query = '';
      this.openIndex = null;
      this.rendered = this.cards.length;
      this.total = this.countEl ? Number(this.countEl.dataset.bpTotal) || this.rendered : this.rendered;

      this.classList.add('bk-parts--ready');

      this.cards.forEach(function (card) {
        // Der Link bleibt im Markup, damit er ohne JavaScript funktioniert.
        // Sobald das Skript läuft, ist es ein Aufklapp-Schalter — und muss
        // sich auch so verhalten, inklusive Leertaste.
        card.setAttribute('role', 'button');
        card.setAttribute('aria-expanded', 'false');
        card.addEventListener('click', function (event) {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
          event.preventDefault();
          this.toggle(card.dataset.bpIndex);
        }.bind(this));
        card.addEventListener('keydown', function (event) {
          if (event.key !== ' ' && event.key !== 'Spacebar') return;
          event.preventDefault();
          this.toggle(card.dataset.bpIndex);
        }.bind(this));
      }, this);

      this.chips.forEach(function (chip) {
        chip.addEventListener('click', function () {
          this.filter = chip.dataset.bpFilter || '';
          this.chips.forEach(function (c) {
            c.setAttribute('aria-pressed', c === chip ? 'true' : 'false');
          });
          this.apply();
        }.bind(this));
      }, this);

      if (this.searchInput) {
        this.searchInput.addEventListener('input', function () {
          this.query = this.searchInput.value.trim().toLowerCase();
          this.apply();
        }.bind(this));
      }

      this.addEventListener('click', function (event) {
        if (event.target.closest('[data-bp-close]')) this.close(true);
      }.bind(this));

      this.onKeydown = function (event) {
        if (event.key === 'Escape' && this.openIndex !== null) this.close(true);
      }.bind(this);
      this.addEventListener('keydown', this.onKeydown);

      this.onResize = function () {
        if (this.openIndex === null || this.resizeFrame) return;
        this.resizeFrame = window.requestAnimationFrame(function () {
          this.resizeFrame = null;
          if (this.openIndex !== null) this.place(this.openIndex);
        }.bind(this));
      }.bind(this);
      window.addEventListener('resize', this.onResize, { passive: true });

      this.apply();

      // Erst jetzt zur Live-Region machen. Stünde aria-live schon im Markup,
      // läse ein Screenreader die Startzahl beim Seitenaufbau als Meldung vor.
      if (this.countEl) this.countEl.setAttribute('aria-live', 'polite');
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
      if (this.resizeFrame) window.cancelAnimationFrame(this.resizeFrame);
      this.close();
    }

    /* --- Filter ---------------------------------------------------------- */

    matches(card) {
      if (this.filter) {
        var models = (card.dataset.bpModels || '').split(/\s+/);
        if (models.indexOf(this.filter) === -1) return false;
      }
      if (this.query) {
        if ((card.dataset.bpSearchText || '').indexOf(this.query) === -1) return false;
      }
      return true;
    }

    apply() {
      var shown = 0;
      this.cards.forEach(function (card) {
        var ok = this.matches(card);
        card.hidden = !ok;
        if (ok) shown += 1;
      }, this);

      if (this.openIndex !== null && this.cards[this.openIndex].hidden) {
        this.close();
      } else if (this.openIndex !== null) {
        this.place(this.openIndex);
      }

      if (this.noneEl) this.noneEl.hidden = shown !== 0;
      if (this.countEl) this.countEl.textContent = this.countText(shown);
    }

    countText(shown) {
      var d = this.countEl.dataset;
      if (shown === this.rendered) {
        return (d.bpFmtAll || '{n}').replace('{n}', this.total).replace('{total}', this.total);
      }
      if (shown === 1) {
        return (d.bpFmtOne || '{n}').replace('{n}', shown).replace('{total}', this.rendered);
      }
      return (d.bpFmtSome || '{n}/{total}').replace('{n}', shown).replace('{total}', this.rendered);
    }

    /* --- Detailkarte ----------------------------------------------------- */

    toggle(index) {
      if (String(this.openIndex) === String(index)) {
        this.close(true);
      } else {
        this.open(index);
      }
    }

    open(index) {
      var i = Number(index);
      var panel = this.panels[i];
      var card = this.cards[i];
      if (!panel || !card) return;

      if (this.openIndex !== null && this.openIndex !== i) this.close();

      this.openIndex = i;
      panel.hidden = false;
      card.setAttribute('aria-expanded', 'true');
      card.classList.add('is-open');
      this.place(i);

      panel.focus({ preventScroll: true });
      var top = panel.getBoundingClientRect().top;
      if (top < 0 || top > window.innerHeight - 120) {
        panel.scrollIntoView({
          behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth',
          block: 'center',
        });
      }
    }

    close(returnFocus) {
      if (this.openIndex === null) return;
      var i = this.openIndex;
      var panel = this.panels[i];
      var card = this.cards[i];
      this.openIndex = null;
      if (panel) {
        panel.hidden = true;
        this.panelStore.appendChild(panel);
      }
      if (card) {
        card.setAttribute('aria-expanded', 'false');
        card.classList.remove('is-open');
        if (returnFocus) card.focus();
      }
    }

    /**
     * Schiebt die Detailkarte als volle Zeile hinter die letzte Kachel
     * derselben Rasterzeile — so klappt sie dort auf, wo geklickt wurde.
     */
    place(index) {
      var panel = this.panels[index];
      var card = this.cards[index];
      if (!panel || !card) return;

      // Die Detailkarte kurz aus dem Raster nehmen, sonst verschiebt sie selbst
      // die Zeilen, an denen gerade gemessen wird.
      var focused = this.contains(document.activeElement) ? document.activeElement : null;
      this.panelStore.appendChild(panel);

      var row = card.offsetTop;
      var last = card;
      this.cards.forEach(function (c) {
        if (!c.hidden && c.offsetTop === row) last = c;
      });
      last.insertAdjacentElement('afterend', panel);

      if (focused && panel.contains(focused)) focused.focus({ preventScroll: true });
    }
  }

  customElements.define('bilga-parts', BilgaParts);
})();
